import { Shape, CanvasContextLike, RgbaColor } from './types';

/**
 * @function renderVectorShapesToCanvas
 * @description Renders an array of vector shapes onto a specific 2D canvas context.
 * This is a pure rendering function that relies strictly on the provided shapes array.
 * @param {CanvasContextLike} targetContext - The target canvas context (2D).
 * @param {Shape[]} shapes - Array of vector shapes to be rendered.
 * @returns {void}
 */
export function renderVectorShapesToCanvas(targetContext: CanvasContextLike, shapes: Shape[]): void {
    if (!shapes || shapes.length === 0) return;

    const context = targetContext as CanvasRenderingContext2D;
    context.save();

    shapes.forEach((shape: Shape) => {
        context.save();

        // STEP 1: Style Setup
        const lineWidth: number = shape.style.width || 1;
        const strokeColor: string = shape.style.color || '#000';
        context.lineWidth = lineWidth;
        context.strokeStyle = strokeColor;
        context.globalAlpha = shape.style.opacity !== undefined ? shape.style.opacity : 1.0;
        context.lineCap = shape.style.cap || 'round';

        // STEP 2: Edge Hardness (via Shadow Blur)
        const hardness: number = shape.style.hardness !== undefined ? shape.style.hardness : 1.0;
        if (hardness < 1.0) {
            const maxBlur: number = Math.max(2, lineWidth / 2);
            context.shadowBlur = (1.0 - hardness) * maxBlur;
            context.shadowColor = strokeColor;
        }

        if (shape.style.fillColor) {
            context.fillStyle = shape.style.fillColor;
        }

        // STEP 3: Transformation (Rotation)
        if (shape.style.angle && shape.type !== 'line') {
            let centerX: number = (shape.x1 + shape.x2) / 2;
            let centerY: number = (shape.y1 + shape.y2) / 2;
            
            // Special handling for circle normalization
            if (shape.type === 'circle') {
                centerX = shape.x1;
                centerY = shape.y1;
            } else {
                centerX = (Math.min(shape.x1, shape.x2) + Math.max(shape.x1, shape.x2)) / 2;
                centerY = (Math.min(shape.y1, shape.y2) + Math.max(shape.y1, shape.y2)) / 2;
            }
            
            context.translate(centerX, centerY);
            context.rotate((shape.style.angle * Math.PI) / 180);
            context.translate(-centerX, -centerY);
        }

        // STEP 4: Path Generation
        context.beginPath();
        switch (shape.type) {
            case 'line':
                // Simple line segment
                context.moveTo(shape.x1, shape.y1);
                context.lineTo(shape.x2, shape.y2);
                context.stroke();
                break;
            case 'rect': {
                // Standard rectangle
                const width: number = shape.x2 - shape.x1;
                const height: number = shape.y2 - shape.y1;
                context.rect(shape.x1, shape.y1, width, height);
                if (shape.style.fill) context.fill();
                context.stroke();
                break;
            }
            case 'circle': {
                // Circle centered at (x1, y1)
                const radius: number = Math.hypot(shape.x2 - shape.x1, shape.y2 - shape.y1);
                context.arc(shape.x1, shape.y1, radius, 0, 2 * Math.PI);
                if (shape.style.fill) context.fill();
                context.stroke();
                break;
            }
            case 'ellipse': {
                // Ellipse centered between points
                const centerX: number = (shape.x1 + shape.x2) / 2;
                const centerY: number = (shape.y1 + shape.y2) / 2;
                const radiusX: number = Math.abs(shape.x2 - shape.x1) / 2;
                const radiusY: number = Math.abs(shape.y2 - shape.y1) / 2;
                context.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
                if (shape.style.fill) context.fill();
                context.stroke();
                break;
            }
            case 'roundrect': {
                // Rounded rectangle with fallback for older browsers
                const rectWidth: number = shape.x2 - shape.x1;
                const rectHeight: number = shape.y2 - shape.y1;
                const cornerRadius: number = shape.style.cornerRadius || 10;
                
                if (typeof (context as any).roundRect === 'function') {
                    (context as any).roundRect(shape.x1, shape.y1, rectWidth, rectHeight, cornerRadius);
                } else {
                    const x = shape.x1, y = shape.y1, w = rectWidth, h = rectHeight, r = cornerRadius;
                    context.moveTo(x + r, y);
                    context.lineTo(x + w - r, y);
                    context.quadraticCurveTo(x + w, y, x + w, y + r);
                    context.lineTo(x + w, y + h - r);
                    context.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
                    context.lineTo(x + r, y + h);
                    context.quadraticCurveTo(x, y + h, x, y + h - r);
                    context.lineTo(x, y + r);
                    context.quadraticCurveTo(x, y, x + r, y);
                }
                if (shape.style.fill) context.fill();
                context.stroke();
                break;
            }
            case 'path': {
                if (shape.pathData) {
                    const p = new Path2D(shape.pathData);
                    if (shape.style.fill) context.fill(p);
                    context.stroke(p);
                }
                break;
            }
        }
        
        context.restore();
    });
    
    context.restore();
}

/**
 * @function parseCssColorToRgba
 * @description Parses a CSS-compatible color string into an RgbaColor object.
 * Utilizes a hidden canvas element for robust browser-native parsing.
 * @param {string} colorString - CSS color string (hex, rgb, rgba, hsl, name).
 * @param {number} [opacity=1.0] - Optional opacity override (0.0 to 1.0).
 * @returns {RgbaColor} An object containing red, green, blue, and alpha components (0-255).
 */
export function parseCssColorToRgba(colorString: string, opacity: number = 1.0): RgbaColor {
    // Default fallback (transparent black)
    const fallback: RgbaColor = { red: 0, green: 0, blue: 0, alpha: 255 };
    
    if (typeof document === 'undefined') return fallback;
    
    const tempCanvas: HTMLCanvasElement = document.createElement('canvas');
    tempCanvas.width = 1;
    tempCanvas.height = 1;
    
    const context: CanvasRenderingContext2D | null = tempCanvas.getContext('2d');
    if (!context) return fallback;
    
    context.fillStyle = colorString;
    context.fillRect(0, 0, 1, 1);
    
    const pixelData: Uint8ClampedArray = context.getImageData(0, 0, 1, 1).data;
    
    return {
        red: pixelData[0],
        green: pixelData[1],
        blue: pixelData[2],
        alpha: Math.floor(opacity * 255)
    };
}
